import { useEffect, useState } from "react";
import { Link, Outlet, NavLink, useLocation } from "react-router-dom";
import { Show } from "@clerk/react";
import { Menu, X } from "lucide-react";
import { usePublic } from "@/lib/api";
import { trackPage } from "@/lib/track";

const NAV = [["/how-it-works", "How it works", "how-it-works"], ["/benefits", "Benefits", "benefits"], ["/pricing", "Pricing", "pricing"], ["/security", "Security"], ["/resources", "Guides"], ["/contact", "Contact"]];

function NavItems({ home, className, prefix, onPick }) {
  const link = ({ isActive }) => `whitespace-nowrap text-sm font-semibold transition-colors ${isActive ? "text-teal-700" : "text-slate-600 hover:text-slate-900"}`;
  return NAV.map(([to, l, anchor]) => (home && anchor
    ? <a key={to} href={`#${anchor}`} onClick={onPick} className={`${className} whitespace-nowrap text-sm font-semibold text-slate-600 transition-colors hover:text-slate-900`} data-testid={`${prefix}-${to.slice(1)}`}>{l}</a>
    : <NavLink key={to} to={to} className={(s) => `${className} ${link(s)}`} data-testid={`${prefix}-${to.slice(1)}`}>{l}</NavLink>));
}

const MobileCta = () => (
  <div className="fixed inset-x-3 bottom-3 z-40 flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white/90 p-2.5 pl-4 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.45)] backdrop-blur-md md:hidden" data-testid="mobile-bottom-cta">
    <p className="text-sm leading-tight"><span className="font-display text-lg font-extrabold">£99</span><span className="text-slate-500"> / month · first branch</span></p>
    <Link to="/signup" className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white" data-testid="mobile-bottom-cta-button">Start now</Link>
  </div>
);

export const useSite = () => usePublic("/public/config");

function Footer({ cfg }) {
  const co = cfg?.company || {};
  return (
    <footer className="border-t border-slate-200 bg-white" data-testid="site-footer">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 md:grid-cols-3">
        <div>
          <p className="font-display text-lg font-extrabold">InvoiceReconciler<span className="text-teal-600">.app</span></p>
          <p className="mt-2 text-sm text-slate-500">Wholesaler invoice reconciliation for UK community pharmacies.</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm" data-testid="footer-legal-links">
          {(cfg?.legal || []).map((d) => <Link key={d.slug} to={`/legal/${d.slug}`} className="text-slate-600 hover:text-teal-700" data-testid={`footer-legal-${d.slug}`}>{d.title}</Link>)}
          <Link to="/legal/archive" className="text-slate-600 hover:text-teal-700">Earlier versions</Link>
        </div>
        <div className="text-xs leading-relaxed text-slate-500" data-testid="footer-company-details">
          <p className="font-semibold text-slate-700">{co.legal_name}</p>
          <p>Company number {co.company_number}, registered in {co.country}</p>
          <p>Registered office: {co.office}</p>
          {co.vat && <p>VAT number {co.vat}</p>}
          {co.ico && <p>ICO registration {co.ico}</p>}
          {co.email && <p>Contact: <a href={`mailto:${co.email}`} className="underline">{co.email}</a></p>}
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  const cfg = useSite();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const home = loc.pathname === "/";
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); return trackPage(); }, [loc.pathname]);
  return (
    <div className="site-lux lux-grain min-h-screen">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-3.5">
          <Link to="/" className="flex shrink-0 items-center gap-2.5" data-testid="site-logo">
            <img src="/icon.svg" alt="" className="h-8 w-8" />
            <span className="font-display text-base font-extrabold sm:text-lg">InvoiceReconciler<span className="text-teal-600">.app</span></span>
          </Link>
          <nav className="hidden items-center gap-7 lg:flex"><NavItems home={home} prefix="site-nav" /></nav>
          <div className="flex items-center gap-3">
            <Show when="signed-in"><Link to="/" className="hidden rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700 sm:inline-flex" data-testid="site-open-app-button">Open the app</Link></Show>
            <Show when="signed-out">
              <Link to="/sign-in" className="hidden text-sm font-semibold text-slate-600 hover:text-slate-900 sm:inline" data-testid="site-sign-in-link">Sign in</Link>
              <Link to="/signup" className="whitespace-nowrap rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-teal-700 sm:px-5 sm:py-2.5" data-testid="site-signup-button">Start now</Link>
            </Show>
            <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="site-menu-button">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
        <nav className="flex gap-5 overflow-x-auto border-t border-slate-100 px-5 py-2.5 pr-10 [mask-image:linear-gradient(to_right,black_85%,transparent)] [scrollbar-width:none] lg:hidden" data-testid="site-mobile-quicknav"><NavItems home={home} prefix="site-quicknav" /></nav>
        {open && (
          <div className="flex flex-col gap-4 border-t border-slate-200 px-5 py-5 lg:hidden" data-testid="site-mobile-menu">
            <NavItems home={home} prefix="site-menu" onPick={() => setOpen(false)} />
            <Show when="signed-in"><Link to="/" className="rounded-full bg-slate-900 px-5 py-3 text-center text-sm font-semibold text-white">Open the app</Link></Show>
            <Show when="signed-out"><Link to="/sign-in" className="text-sm font-semibold" data-testid="site-menu-sign-in">Sign in</Link></Show>
          </div>
        )}
      </header>
      <main><Outlet context={cfg} /></main>
      <Footer cfg={cfg} />
      {!loc.pathname.startsWith("/signup") && <Show when="signed-out"><MobileCta /></Show>}
      <div className="h-20 md:hidden" />
    </div>
  );
}
